import sentryBeforeSend from "@/utils/sentryBeforeSend";
import * as Sentry from "@/observability/sentry";
// eslint-disable-next-line no-restricted-imports
import posthog from "posthog-js";

// `typeof window` guard added at the TanStack cutover: Next loaded this file
// only in the browser, whereas the module graph here reaches it during SSR too,
// and the browser SDK must not initialise on the server.
if (
  typeof window !== "undefined" &&
  (process.env.NEXT_PUBLIC_SENTRY_ENVIRONMENT === "production" ||
    process.env.NODE_ENV === "production")
) {
  Sentry.init({
    dsn: "https://465fe54d3cae462d8d002e6a525fbf92@o73057.ingest.sentry.io/4503931616100352",
    release: process.env.NEXT_PUBLIC_SENTRY_RELEASE,
    environment: process.env.NEXT_PUBLIC_SENTRY_ENVIRONMENT,
    tracesSampleRate: 0,
    beforeSend: sentryBeforeSend,
    ignoreErrors: [
      // Random plugins/extensions
      "top.GLOBALS",
      // See: http://blog.errorception.com/2012/03/tale-of-unfindable-js-error.html
      "originalCreateNotification",
      "canvas.contentDocument",
      "i.contentDocument",
      "MyApp_RemoveAllHighlights",
      "http://tt.epicplay.com",
      "jigsaw is not defined",
      "ComboSearch is not defined",
      "http://loading.retry.widdit.com/",
      "atomicFindClose",
      // Facebook borked
      "fb_xd_fragment",
      // ISP "optimizing" proxy - `Cache-Control: no-transform` seems to reduce this. (thanks @acdha)
      // See http://stackoverflow.com/questions/4113268/how-to-stop-javascript-injection-from-vodafone-proxy
      "bmi_SafeAddOnload",
      "EBCallBackMessageReceived",
      // See http://toolbar.conduit.com/Developer/HtmlAndGadget/Methods/JSInjection.aspx
      "conduitPage",
      // Generic error code from errors outside the security sandbox
      // You can delete this if using raven.js > 1.0, which ignores these automatically.
      "Script error.",
      // Avast extension error
      "_avast_submit",
      "Non-Error exception captured",
      "Non-Error promise rejection captured",
      "ResizeObserver loop limit exceeded",
      "Cannot redefine property: ethereum",
      "Cannot redefine property: solana",
      "Blocked a frame with origin",
      "msDiscoverChatAvailable",
      "ResizeObserver",
      "NetFunnel is not defined",
      "QuotaExceededError",
      "Talisman extension",
      "win is not defined",
      "redeclaration of const hideMyLocation",
      "TypeError: Failed to fetch",
      "TypeError: Load failed", // iOS Safari WebKit equivalent of "Failed to fetch" — third-party (Klaviyo CDN, GTM) background fetches; our app uses axios which surfaces failures as AxiosError: Network Error
      "TypeError: Properties can only be defined on Objects.",
      'Permission denied to access property "unlock"',
      "uv.handler",
      "removeAllUnloadsIframes",
      "<anonymous>",
      "<unknown>",
      "getReadModeConfig",
      "getReadModeExtract",
      "getReadModeRender",
      "nexusWebSocket",
      "transover-type-and-translate-popup",
      "transover-popup",
      "grecaptcha",
      "popperjs",
      "missing ) after argument list",
      /null is not an object \(evaluating 'window\.parent\.location'\)/,
      /Illegal invocation/,
      /SecurityError: Permission denied to access property "navigator" on cross-origin object/,
      /SecurityError: Permission denied to access property "227g" on cross-origin object/,
      /NotAllowedError: Clipboard write was blocked due to lack of user activation/,
      /SecurityError: The operation is insecure/,
      /SecurityError: Permission denied to access property "g1d5" on cross-origin object/,
      /SecurityError: Permission denied to define property "Date" on cross-origin object/,
      /TypeError: can't redefine non-configurable property "solana"/,
      /TypeError: can't redefine non-configurable property "ethereum"/,
      /SecurityError: Permission denied to access property "pfv" on cross-origin object/,
      /TypeError: Cannot redefine property: userAgent/,
      /TypeError: null is not an object \(evaluating 'parent.document'\)/,
      /Error: Menu caption text is required!/,
      /TypeError: selectElement is null/,
      /TypeError: Cannot read properties of null \(reading 'offsetTop'\)/,
      /Error: Cancel rendering route/,
      /TypeError: native is undefined/,
      /TypeError: WeakMap key undefined must be an object or an unregistered symbol/,
      /TypeError: Property descriptor must be an object, got undefined/,
      /ReferenceError: tgetT is not defined/,
      /twq is not defined/, // Twitter/X ads pixel fired via GTM; static.ads-twitter.com is blocked by Firefox tracking protection / adblockers (~95% Firefox), so the twq stub is never defined and the GTM conversion tag throws
      /TypeError: window.ethereum.setConfig is not a function/,
      /TypeError: Cannot read properties of undefined \(reading 'sendMessage'\)/,
      /TypeError: undefined is not an object \(evaluating 'window\.webkit\.messageHandlers/, // Facebook / iOS in-app browser injected userScripts
      /Error: Permission denied to access property "__SENTRY_LOADER__"/,
      /Error: frame window is not ready/,
      /TypeError: 'TRACE' HTTP method is unsupported./,
      /TypeError: can't redefine non-configurable property "adoptedStyleSheets"/,
      /TypeError: setting getter-only property "chainId"/,
      /Error: Permission denied to access property "getAttribute"/, // https://github.com/mui/material-ui/issues/41514
      /TypeError: Cannot read properties of undefined \(reading 'Symbol\(\)'\)/,
      /TypeError: Blob is not a constructor/,
      /SecurityError: Permission denied to access property "3x5v" on cross-origin object/,
      /<unknown>/,
      /SyntaxError: Unexpected end of input/,
      /SyntaxError: No identifiers allowed directly after numeric literal/,
      /TypeError: \w.focus is not a function/,
      /Error: Invalid call to runtime.sendMessage\(\). Tab not found./,
      /Error: Invalid call to runtime.sendMessage\(\). The 'extensionID' value is invalid, because cross-extension messaging is not supported./,
      /TypeError: oldDescriptor is undefined/,
      /AxiosError: timeout exceeded/,
      /AxiosError: Network Error/,
      /AxiosError: Request failed with status code 500/,
      /AxiosError: Request failed with status code 401/,
      /AxiosError: Request failed with status code 400/,
      /AxiosError: Request aborted/,
      /TypeError: can't access property "dropEffect", i.dataTransfer is null/,
      /SyntaxError: Identifier 'wsAllowlisted' has already been declared/,
      /TypeError: ReadableStream is locked/,
      /Error: Cannot parse given Error object/,
      /TypeError: i.dataTransfer is null/,
      /Error: MUI X: The token yyyy should have a 'maxDigitNumber' property on it's adapter/,
      /Can't find variable: /, // https://support.google.com/tagmanager/thread/325457635/how-to-solve-error-can-t-find-variable-gmo-on-gtm-server-side?hl=en
      /TypeError: 'getElementsByTagName' called on an object that does not implement interface Element./,
      /Error: _error.js called with falsy error \(undefined\)/i, // https://github.com/getsentry/sentry-javascript/discussions/9038
      /SyntaxError: Unexpected EOF/, // This is caused by network issues
      /AbortError: signal is aborted without reason/,
      /Abort fetching component for route:/,
      /feature named.*was not found/, // Third-party script error (GTM/analytics), mainly on DuckDuckGo Mobile browser
      /Error invoking post: Method not found/, // Android WebView JavaScript bridge error (injected code)
      /Java bridge method invocation error/, // Android WebView JavaScript bridge error (injected code)
      /Java object is gone/, // Android WebView JavaScript bridge error (injected code)
      /No error message/, // Cross-origin DOMException from GTM/FB Pixel monkey-patching HTMLFormElement.prototype.submit
      /Invariant: attempted to hard navigate to the same URL/, // Next.js internal error when stale chunks cause routing to /404 after deployments
      /Event `(?:XMLHttpRequest)?ProgressEvent`/, // XMLHttpRequest network failure captured as unhandled promise rejection (no stacktrace, no debug value)
      /RangeError: Maximum call stack size exceeded/, // React internal scheduler recursion on certain mobile iOS browsers (visibilitychange event)
      /SecurityError: Failed to read the 'localStorage' property from 'Window'/, // posthog-js surveys visibilitychange handler when the browser blocks localStorage (mobile Chrome privacy settings) — out of our control, harmless
      /tns is not defined/, // proxybrowser injected script
      /window\.ethereum\.selectedAddress/, // crypto wallet extension
      /mtcaptchaInstance is undefined/, // mtcaptcha third-party widget
      /Cannot read properties of undefined \(reading 'graphToken'\)/, // Facebook Pixel (fbevents) third-party
      /Saving event cache due to failed request/, // third-party analytics (post_identification_sync)
      /SecurityError: Failed to read a named property/, // posthog recorder cross-origin iframe access
      /SecurityError: An attempt was made to break through the security policy of the user agent/, // posthog recorder cross-origin iframe access
      /RangeError: invalid field value/, // posthog-js UUIDv7 generator bug (fromFieldsV7 "not 128-bit length") — thrown deep inside posthog.capture(), no fix available in our code
      /SecurityError: Permission to call ['‘"]get parentNode['’"] denied/, // posthog-js rrweb recorder touching extension-inserted DOM on Firefox (DOMException 18, zero app frames)
    ],
    denyUrls: [
      // Google Adsense
      /pagead\/js/i,
      // Facebook flakiness
      /graph\.facebook\.com/i,
      // Facebook blocked
      /connect\.facebook\.net\/en_US\/all\.js/i,
      // Woopra flakiness
      /eatdifferent\.com\.woopra-ns\.com/i,
      /static\.woopra\.com\/js\/woopra\.js/i,
      // Chrome extensions
      /extensions\//i,
      /^chrome:\/\//i,
      /^chrome-extension:\/\//i,
      /^safari-web-extension:\/\//i,
      // Other plugins
      /127\.0\.0\.1:4001\/isrunning/i, // Cacaoweb
      /webappstoolbarba\.texthelp\.com\//i,
      /metrics\.itunes\.apple\.com\.edgesuite\.net\//i,
      "recaptcha/releases",
      "userscript",
      "translate_http",
      "maps/api/js",
      "recorder-v2",
      "recorder-v2.js",
      "recorder.js",
      "user-guiding",
      "posthog-js",
      "useGridTreeDataPreProcessors.js",
      "inject.js",
      "ChartUtils",
      "d3-scale",
      "_ssgManifest",
      "ucbrowser",
      "vim.js",
      "popperjs",
      "widget-v2",
      /fender_analytics/i,
      /post_identification_sync/i,
      /fbevents/i,
      /analytics\.google\.com/i,
      /googletagmanager/i,
      /hammerhead/i,
      /mt_captcha/i,
      /klaviyo/i, // third-party Klaviyo onsite script injected by some Electron clients
    ],
    integrations: [
      posthog.sentryIntegration({
        organization: "webshare-proxy",
        projectId: 4503931616100352,
      }),
    ],
  });
}
